/**
 * AutoGrowTextarea - A textarea whose height tracks its scrollHeight on
 * every input, so long pasted content expands the field instead of
 * scrolling inside it. Behaves like a doc-style input rather than a
 * fixed-height form field.
 *
 * The component is fully controlled — the parent owns the `value` and
 * receives change events via `onChange(next: string)`. A ref is used
 * internally to manipulate the textarea's height after value changes
 * (including external value writes).
 *
 * Used by:
 *   - CMSPage / ChecklistEditor (description + item details + callout)
 *   - Anywhere a long-form text input should grow with content
 *
 * @example
 * <AutoGrowTextarea
 *     value={notes}
 *     onChange={setNotes}
 *     placeholder="Notes..."
 *     minRows={3}
 * />
 */
import React, { useCallback, useEffect, useRef } from 'react';

/**
 * Props for AutoGrowTextarea.
 *
 * @property value        Current textarea content (controlled)
 * @property onChange     Called with the next string value on input
 * @property placeholder  Placeholder text shown when value is empty
 * @property minRows      Initial visible row count before auto-grow
 * @property className    Optional override classes (merged with defaults)
 * @property disabled     When true, the textarea is read-only and dimmed
 */
export interface AutoGrowTextareaProps {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    minRows?: number;
    className?: string;
    disabled?: boolean;
}

/* Base look. Callers' `className` is merged AFTER this (never instead of it),
   so a caller can recolor the border for an error state without losing
   w-full / resize-none / the auto-grow overflow handling. */
const BASE_CLASS = 'w-full px-12px py-8px border border-brand-border rounded-md text-16px font-sans text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none overflow-hidden';

export const AutoGrowTextarea: React.FC<AutoGrowTextareaProps> = ({
    value,
    onChange,
    placeholder,
    minRows = 2,
    className,
    disabled = false,
}) => {
    /* DOM ref used to read scrollHeight and write the new height on input */
    const ref = useRef<HTMLTextAreaElement | null>(null);

    /* Resize the element to fit its current content. Resets height to
     * 'auto' first so the scrollHeight measurement reflects the actual
     * content size (including any shrinkage). */
    const resize = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        el.style.height = 'auto';

        /* scrollHeight covers content + padding but NOT the border, and
         * Tailwind's preflight puts every element in border-box — so the
         * borders have to be added back or the field loses ~2px of content
         * area on every keystroke and clips the last line. */
        const styles = window.getComputedStyle(el);
        const borders = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
        const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);

        /* Floor the height at `minRows` lines. Setting height from
         * scrollHeight alone defeats the rows attribute — an empty field
         * measures one line and collapses, however many rows were asked for. */
        const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;
        const minHeight = lineHeight * minRows + padding + borders;

        el.style.height = `${Math.max(el.scrollHeight + borders, minHeight)}px`;
    }, [minRows]);

    /* Re-resize when value changes externally (e.g., parent prefill). */
    useEffect(() => {
        resize();
    }, [value, resize]);

    /* Width changes re-wrap the text, which changes the line count — rotating
     * a phone or resizing the window has to re-measure. */
    useEffect(() => {
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, [resize]);

    return (
        <textarea
            ref={ref}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            onInput={resize}
            rows={minRows}
            placeholder={placeholder}
            data-ref="atom-auto-grow-textarea"
            className={`auto-grow-textarea ${BASE_CLASS} ${className ?? ''}`}
        />
    );
};

export default AutoGrowTextarea;
